import { useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import * as L from "leaflet";

import { useSubstances } from "src/hooks/useSubstances";
import { PointSourceForm, PointSourceFormData } from "src/forms/PointSourceForm";
import { usePointSourceCreateMutation } from "src/hooks";
import { latlngToWkt } from "src/utils/geometry.utils";
import { getLeafletMarker } from "src/utils/marker.utils";
import { useTranslation } from "react-i18next";
import { useTimefactors } from "src/hooks/timefactorHooks";

interface Props {
  map: L.Map;
  onClose: () => void;
}

export function PointSourceEmissionPointSourceCreatePage(props: Props) {
  const { map } = props;

  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();
  const {
    caseId = "",
    scenarioId = "",
    emissionId = "",
  } = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
  }>();

  const { data: substances } = useSubstances();
  const { data: timefactors } = useTimefactors();
  const createMutation = usePointSourceCreateMutation(scenarioId, emissionId);

  usePointSourceVisualisation(map, location.state?.point);

  /**
   * Functions
   */
  async function handleSave(values: PointSourceFormData) {
    if (!location.state.point) return;

    const point_frontendSrid = latlngToWkt(location.state.point);
    const result = await createMutation.mutateAsync({
      ...values,
      point_frontendSrid,
    });

    // use timeout for API create to be ressembled in geoserver database
    function redirect() {
      navigate(
        `/cases/${caseId}/scenarios/${scenarioId}/emissions/point-source/${emissionId}/point-sources/${result.id}`,
        { state: { action: "rerenderWMS" } }
      );
    }
    setTimeout(redirect, 1000);

    // remove drawn layer, the geometry is now persisted and visualized as part of the WMSLayer
    // @ts-ignore
    props.map?.pm.getGeomanDrawLayers().forEach((layer) => layer.remove());
  }

  return render();

  function render() {
    if (!substances || !timefactors) return null;

    return (
      <PointSourceForm
        timefactors={timefactors || []}
        title={t("label.addPointSource")}
        data={{
          name: "",
          height_m: 0,
          diameter_m: 0,
          temperature_K: 0,
          flowrate_Nm3_s: 0,
          nonox: 0,
          ifdmTimeFactor: "",
          emissions_kg_h: substances.map((substance) => ({
            substance: substance.key,
            emission_kg_h: 0,
          })),
        }}
        substances={substances}
        onClose={() => props.onClose()}
        onSubmit={handleSave}
        isLoading={createMutation.isLoading}
        errors={createMutation.error?.response?.data}
      />
    );
  }
}

function usePointSourceVisualisation(map: L.Map, pointSource: L.LatLngLiteral | undefined) {
  const featureGroup = L.featureGroup();

  /**
   * Effects
   */
  useEffect(addFeatureGroupToMap, [featureGroup, map]);
  useEffect(() => {
    // const featureLayer = L.geoJSON(feature.geometry, {
    //   pointToLayer: (geoJsonPoint, latlng) =>
    //     getPointSourceMarker(latlng, "new"),
    // });
    if (!pointSource) return;
    const marker = getLeafletMarker(pointSource, "new", {});
    featureGroup.addLayer(marker);
  });

  /**
   * Functions
   */

  function addFeatureGroupToMap() {
    featureGroup.addTo(map);

    return function cleanup() {
      featureGroup.removeFrom(map);
    };
  }
}
